import {t} from '@/i18n'
import {ColoredOption} from '@/types/ColoredOption'
import {optionOf, optionsOf, reaches} from '@/types/Ladder'
import {Person} from '@/types/Person'

/**
 * A admissão de uma conta no lunedi, que está em fase de convite: cadastrar-se não basta.
 *
 * Outra escada, e outro assunto, que a do `AccessLevel`: aquela diz o que alguém pode **dentro de
 * um ambiente**, esta diz se a pessoa entra no sistema. `member` aqui é "membro do lunedi", e não o
 * `Member` de um ambiente — as duas listas nunca se cruzam.
 *
 * Como a dos níveis de acesso, está escrita **duas vezes**, uma em cada repositório: o
 * `utils/accountAccess.ts` do servidor é a garantia, este é o desenho.
 */
export const ACCESS = ['pending', 'member', 'approver'] as const
export type Access = typeof ACCESS[number]

/** A conta como a tela a vê: a identidade e o nível. */
export type Account = Person & {access: Access}

export function allows(access: Access | undefined, needed: Access): boolean {
    return reaches(ACCESS, access, needed)
}

/**
 * Esta recusa é a do convite que ainda espera aprovação?
 *
 * Mora aqui, com o resto do vocabulário da admissão, porque **três lugares perguntam o mesmo**: o
 * `RouteComponent`, que manda ao login quem foi devolvido a pendente com a tela aberta; o botão
 * do Google, que precisa distinguir esta recusa das do provedor; e o cadastro, que por causa dela
 * troca de tela. Escrito três vezes, o literal do servidor estaria em três arquivos.
 */
export function notApproved(err: any): boolean {
    return err?.data?.validation === 'not-approved'
}

/** Esta pessoa administra convites? É o que decide o item do menu e o guarda da rota. */
export function isApprover(user: {access?: Access} | null): boolean {
    return allows(user?.access, 'approver')
}

/**
 * As cores dizem o mesmo que nas outras escadas: **roxo administra** — dentro de um ambiente ou
 * fora dele — e **índigo usa**.
 *
 * O âmbar é só do pendente, e é a única cor do app que pede ação: esta tela é uma fila de
 * trabalho, e o que espera por alguém tem de saltar dela. Cor base, sem tom, como o `ROLE_COLOR`.
 */
export const ACCESS_COLOR: Record<Access, string> = {
    pending: 'amber',
    member: 'indigo',
    approver: 'deep-purple'
}

export function accessName(access: Access): string {
    return t(`access-${access}`) as string
}

export function accessOption(access: Access): ColoredOption {
    return optionOf(ACCESS, ACCESS_COLOR, accessName, access)
}

export function accessOptions(): Array<ColoredOption & {value: Access}> {
    return optionsOf(ACCESS, ACCESS_COLOR, accessName)
}
